import { useEffect, useRef } from 'react'

const EVENT_TYPE_CONFIG = {
  FILE_CHANGE: { label: 'FILE', color: 'text-indigo border-indigo bg-indigo-faint' },
  TOOL_CALL: { label: 'TOOL', color: 'text-accent border-accent bg-accent-faint' },
  MEMORY_UPDATE: { label: 'MEM', color: 'text-green border-green bg-green-faint' },
  CHECKPOINT: { label: 'SNAP', color: 'text-yellow border-yellow bg-yellow-faint' },
}

const FALLBACK_CONFIG = { label: 'EVT', color: 'text-text-dim border-border bg-surface2' }

function formatTimestamp(dateStr) {
  if (!dateStr) return '??:??:??'
  const d = new Date(dateStr)
  return d.toLocaleTimeString(undefined, { hour12: false })
}

function summarisePayload(payload) {
  if (!payload || typeof payload !== 'object') return ''
  const keys = Object.keys(payload)
  if (keys.length === 0) return ''
  // Show first two key=value pairs, truncated
  return keys
    .slice(0, 2)
    .map((k) => {
      const v = String(payload[k])
      return `${k}=${v.length > 24 ? v.slice(0, 24) + '…' : v}`
    })
    .join('  ')
}

function TelemetryRow({ event }) {
  const config = EVENT_TYPE_CONFIG[event.eventType] ?? FALLBACK_CONFIG
  const summary = summarisePayload(event.payload)

  return (
    <div className="flex items-baseline gap-3 py-1.5 px-3 border-b border-border hover:bg-surface2 transition-colors group">
      <span className="text-2xs font-mono text-text-faint flex-shrink-0 w-16">
        {formatTimestamp(event.recordedAt)}
      </span>
      <span
        className={`text-2xs font-mono font-semibold border rounded px-1.5 py-0.5 flex-shrink-0 tracking-widest ${config.color}`}
      >
        {config.label}
      </span>
      <span className="text-xs font-mono text-text-dim truncate flex-1">
        {summary || event.eventType}
      </span>
      {event.agentId && (
        <span className="text-2xs font-mono text-text-faint flex-shrink-0 hidden group-hover:block">
          {event.agentId.slice(0, 10)}
        </span>
      )}
    </div>
  )
}

const MAX_EVENTS = 200

export default function TelemetryStream({ events, loading, connected, usingFallback }) {
  const bottomRef = useRef(null)
  const containerRef = useRef(null)
  const autoScrollRef = useRef(true)

  const capped = events.slice(-MAX_EVENTS)

  // Auto-scroll to bottom when new events arrive
  useEffect(() => {
    if (!autoScrollRef.current) return
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [capped.length])

  const handleScroll = () => {
    if (!containerRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current
    autoScrollRef.current = scrollHeight - scrollTop - clientHeight < 40
  }

  return (
    <div className="flex flex-col h-full border border-border rounded-lg overflow-hidden bg-surface">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-surface2 flex-shrink-0">
        <span className="text-2xs font-mono text-text-faint tracking-widest uppercase">
          Telemetry stream
        </span>
        <div className="flex items-center gap-2">
          <span className="text-2xs font-mono text-text-faint">
            {capped.length} events
          </span>
          {connected ? (
            <span className="flex items-center gap-1 text-2xs font-mono text-green">
              <span className="w-1.5 h-1.5 rounded-full bg-green animate-pulse" aria-hidden="true" />
              live
            </span>
          ) : usingFallback ? (
            <span className="flex items-center gap-1 text-2xs font-mono text-yellow">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow" aria-hidden="true" />
              polling
            </span>
          ) : (
            <span className="text-2xs font-mono text-text-faint">offline</span>
          )}
        </div>
      </div>

      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto min-h-0"
        aria-label="Telemetry events"
        aria-live="polite"
        aria-atomic="false"
      >
        {loading && capped.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <span className="text-xs font-mono text-text-faint">Loading…</span>
          </div>
        ) : capped.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 gap-1">
            <span className="text-xs text-text-dim">No events yet</span>
            <span className="text-2xs font-mono text-text-faint">Waiting for agent activity</span>
          </div>
        ) : (
          capped.map((event) => (
            <TelemetryRow key={event.id} event={event} />
          ))
        )}
        <div ref={bottomRef} aria-hidden="true" />
      </div>
    </div>
  )
}
